<script setup lang="ts">
const route = useRoute()

const { locale } = useI18n()

const { globalData } = useVildmarkenState()
const _settings = ref(globalData.value?.settings)
const data = ref(globalData.value?.navigation)

const localePath = useLocalePath()

const { theme } = useTheme()

const { y } = useWindowScroll()
const { width } = useWindowSize()

const openMenu = ref('')
const burgerMenuOpen = ref(false)
const searchOpen = ref(false)

function toggleMenu(menuId: string) {
  burgerMenuOpen.value = false
  searchOpen.value = false

  if (openMenu.value === menuId) {
    openMenu.value = ''
  } else {
    openMenu.value = menuId
  }
}

const isScrolled = computed(() => y.value > 50)

function toggleBurgerMenu() {
  openMenu.value = ''
  burgerMenuOpen.value = !burgerMenuOpen.value
}
function toggleSearch() {
  openMenu.value = ''
  searchOpen.value = !searchOpen.value
  burgerMenuOpen.value = false
}

function closeMenus() {
  openMenu.value = ''
  burgerMenuOpen.value = false
}

watch(width, closeMenus)
watch(() => route.path, closeMenus)
watch(locale, () => {
  console.log('locale changed, reload navigation and settings data', locale.value)
  refreshSettings()
  refreshNavigation()
})

const activeFirstLevelLink = computed(() => {
  let activeLink = null

  data?.value?.megaMenu?.forEach((firstLevelItem) => {
    firstLevelItem.items?.forEach((secondLevelGroup) => {
      secondLevelGroup.items?.forEach((link) => {
        if (link.page?.url === route.path) {
          activeLink = firstLevelItem
        }

        // check if the current path includes routes that should activate the top level item
        const routesToActivate = firstLevelItem.routesToActivate?.split(',') || []

        if (routesToActivate.length && routesToActivate.some((str) => route.path.includes(str))) {
          activeLink = firstLevelItem
        }
      })
    })
  })

  return activeLink
})
const error = useError()
const headerThemeClass = computed(() => {
  const state =
    openMenu.value || error.value
      ? 'menuOpen'
      : burgerMenuOpen.value || searchOpen.value
        ? 'burgerOpen'
        : !isScrolled.value && !burgerMenuOpen.value && !openMenu.value
          ? 'inTop'
          : isScrolled.value && !burgerMenuOpen.value && !openMenu.value
            ? 'scrolled'
            : ''

  const themes = {
    menuOpen: 'theme-green-light',
    burgerOpen: 'theme-green-dark',
    inTop: route?.name.includes('article-slug')
      ? `theme-${theme.value} theme-transparent-dark !bg-transparent`
      : `theme-${theme.value} theme-transparent-light !bg-transparent`,
    scrolled: `theme-${theme.value}`,
  }

  return themes[state]
})
async function refreshSettings() {
  const { data: newSettings } = await useSanityQuery(getSettings, { language: locale.value })
  _settings.value = newSettings.value
}

async function refreshNavigation() {
  const { data: newNavigation } = await useContent(getNavigation)
  data.value = newNavigation.value
}

provide('search-open', searchOpen)
</script>

<template>
  <header
    v-if="data"
    :class="headerThemeClass"
    class="px-bleed after:w-cols-4 fixed top-0 left-0 z-20 flex h-16 w-screen items-center justify-between gap-x-5 bg-(--theme-bg) text-(--theme-fg) transition-colors after:absolute after:bottom-0 after:z-4 after:h-px after:grid-rows-2 after:bg-(--theme-fg)/15 lg:grid lg:grid-cols-12 lg:after:col-span-9 lg:after:col-start-4 lg:after:w-full xl:after:col-span-6 xl:after:col-start-7"
  >
    <!-- Homepage link / Logo -->
    <nuxt-link
      v-show="!searchOpen"
      :to="localePath('/')"
      aria-label="Go to frontpage"
      class="relative z-5 col-span-6 lg:z-2 lg:col-span-3 xl:col-span-6"
    >
      <VildmarkenLogo v-if="locale == 'da'" class="h-4 w-auto" />
      <WildernessLogo v-if="locale == 'en'" class="h-4 w-auto" />
    </nuxt-link>

    <div
      class="col-span-6 flex h-full w-full items-center justify-end lg:col-span-9 lg:justify-between xl:col-span-6"
    >
      <!-- Menu triggers -->
      <nav
        v-show="!searchOpen"
        class="z-5 col-span-3 flex h-full gap-8 text-sm transition-colors max-lg:hidden"
      >
        <button
          v-for="megaMenu in data?.megaMenu"
          :key="megaMenu._key"
          class="relative flex cursor-pointer items-center justify-center text-base font-medium after:absolute after:bottom-0 after:block after:h-1 after:w-full after:bg-current after:opacity-0 after:transition-opacity hover:after:opacity-100"
          :class="[
            {
              'after:opacity-100 hover:after:opacity-100':
                openMenu === megaMenu._key || activeFirstLevelLink?.title === megaMenu.title,
            },
          ]"
          :aria-controls="`#${megaMenu._key}`"
          :aria-expanded="openMenu === megaMenu._key"
          @click="() => toggleMenu(megaMenu._key)"
        >
          {{ megaMenu.title }}
          <IconsChevronDown
            class="ml-2 w-4 origin-center transition-transform"
            :class="openMenu === megaMenu._key ? 'rotate-180' : ''"
          />
        </button>
      </nav>

      <div v-show="!searchOpen" class="z-5 hidden lg:flex">
        <CommonBasicLink
          v-for="(cta, index) in data?.callToAction"
          :key="index"
          :link="cta"
          class="rounded-sm bg-(--theme-fg) px-4.5 py-1.5 font-medium text-(--theme-fg-inverted)"
        >
          {{ cta.label }}</CommonBasicLink
        >
      </div>

      <!-- Menus -->
      <div
        class="no-scrollbar absolute top-0 right-0 w-screen overflow-auto overscroll-y-contain bg-(--theme-bg) text-(--theme-fg) opacity-0 transition-opacity max-lg:hidden"
        :class="[
          {
            'pointer-events-none': !openMenu && !burgerMenuOpen && !searchOpen,
            'opacity-100': openMenu || burgerMenuOpen || searchOpen,
          },
        ]"
      >
        <div v-for="(megaMenu, index) in data?.megaMenu" v-show="!searchOpen" :key="index">
          <aside
            v-show="openMenu === megaMenu._key"
            :id="megaMenu._key"
            :aria-expanded="openMenu === megaMenu._key"
            class="z-2 grid min-h-[70vh] w-full grid-cols-2 items-start gap-(--grid-gap)"
          >
            <div class="pl-bleed pb-10 lg:pt-32">
              <div v-if="megaMenu.socialMediaCard" class="w-cols-4 overflow-clip rounded-sm">
                <CardsSocialMediaEvent
                  :social-media-event="megaMenu.socialMediaCard[0]"
                  :style="`--theme-card: var(--green-3)`"
                />
              </div>
              <div
                v-else-if="megaMenu?.media"
                class="w-cols-4 aspect-[4/3] overflow-clip rounded-sm"
              >
                <CommonMedia
                  :media="megaMenu.media"
                  class="h-full w-full object-cover"
                  :sizes="'xs:25vw sm:25vw md:25vw lg:25vw xl:25vw xxl:25vw'"
                />
              </div>
            </div>

            <div
              class="sticky top-0 col-start-2 grid w-full grid-cols-2 gap-(--grid-gap) pt-16 pr-5 pb-10 lg:pt-32"
            >
              <nav v-for="menu in megaMenu?.items" :key="menu._key">
                <h2 v-if="menu.title" class="font-display mb-6 text-lg">
                  {{ menu.title }}
                </h2>
                <ElementsLinkList :items="menu.items" :mark-active="true" />
              </nav>
            </div>
          </aside>
        </div>
        <div>
          <aside
            v-show="burgerMenuOpen && !searchOpen"
            :key="'burgerMenuOpen'"
            :aria-expanded="burgerMenuOpen"
            class="z-2 grid min-h-[70vh] w-full grid-cols-2 items-start gap-(--grid-gap)"
          >
            <div class="pl-bleed pb-10 lg:pt-32">
              <div
                v-if="data?.burgerMenu?.media"
                class="w-cols-4 aspect-[4/3] overflow-clip rounded-sm"
              >
                <CommonMedia :media="data?.burgerMenu.media" class="h-full w-full object-cover" />
              </div>
            </div>
            <div
              class="pr-bleed sticky top-0 col-start-2 grid h-full w-full grid-cols-1 gap-(--grid-gap) pt-16 pb-15 lg:pt-32"
            >
              <nav>
                <ElementsLinkList :items="data?.burgerMenu?.links" :mark-active="true" />
              </nav>
              <nav class="mt-auto">
                <CommonBasicLink
                  v-for="(item, index) in data?.burgerMenu?.arrowLinks"
                  :key="index"
                  :link="item"
                  class="flex w-full items-center justify-between border-t border-(--theme-fg)/15 py-5 text-base"
                >
                  {{ item.label }}
                  <IconsArrowRight class="size-4" />
                </CommonBasicLink>
                <div
                  class="language-wrapper font-base flex w-full items-center justify-between border-t border-(--theme-fg)/15 py-5 text-base font-medium"
                >
                  <LanguageSelector />
                  <IconsArrowRight class="size-4" />
                </div>
              </nav>
            </div>
          </aside>
          <aside
            v-show="searchOpen"
            :key="'searchOpen'"
            :aria-expanded="searchOpen"
            class="relative z-10 h-[100dvh] w-full items-start bg-(--green-1)"
          >
            <div class="px-bleed w-full">
              <Search
                :suggestions="data?.searchSuggestions"
                :suggestions-text="data?.searchSuggestionsText"
                @close="() => toggleSearch()"
              />
            </div>
          </aside>
        </div>
      </div>

      <!-- Menu backdrop -->
      <Transition name="backdrop-transition">
        <!-- NEED style backdrop blur to work in safari... -->
        <div
          v-show="openMenu || burgerMenuOpen || searchOpen"
          class="no-scrollbar absolute top-0 left-0 z-[-1] h-screen w-screen overflow-auto overscroll-y-contain backdrop-blur-sm max-lg:hidden"
          style="-webkit-backdrop-filter: blur(8px); backdrop-filter: blur(8px)"
          @click="() => toggleMenu('')"
        >
          <div class="h-[101vh] w-full bg-(--green-1)/15" />
        </div>
      </Transition>

      <!-- Search / Pin links -->
      <div
        v-show="!searchOpen"
        class="relative z-5 col-span-2 flex h-full items-center justify-end gap-3 transition-colors lg:gap-(--grid-gap)"
        :class="[
          {
            'ml-auto': searchOpen,
          },
        ]"
      >
        <button
          aria-label="Open and close the search"
          class="search-button cursor-pointer"
          @click="() => toggleSearch()"
        >
          <IconsCross v-if="searchOpen" class="size-5 lg:size-4" />
          <IconsSearch v-else class="size-5 lg:size-4" />
        </button>
        <button
          v-show="!searchOpen"
          aria-label="Open and close the menu"
          class="menu-buttoncursor-pointer"
          @click="() => toggleBurgerMenu()"
        >
          <IconsCross v-if="burgerMenuOpen" class="size-5 lg:size-4" />
          <IconsHamburger v-else class="size-5 lg:size-4" />
        </button>
      </div>
    </div>

    <!-- Mobile menu -->

    <MobileMenu
      class="transition-opacity lg:hidden"
      :class="[
        burgerMenuOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
      ]"
      :mega-menus="data?.megaMenu"
      :burger-items="data?.burgerMenu?.links"
      :arrow-links="data?.burgerMenu?.arrowLinks"
      :open="burgerMenuOpen"
      :call-to-action="data?.callToAction"
    />
    <MobileSearch
      class="transition-opacity lg:hidden"
      :class="[searchOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0']"
      :open="searchOpen"
      :suggestions="data?.searchSuggestions"
      :suggestions-text="data?.searchSuggestionsText"
      @close="() => toggleSearch()"
    />
  </header>
</template>

<style lang="postcss">
/**
 * transition of the blurry background
 */
.backdrop-transition-enter-active {
  transition: opacity 0.2s ease-in-out;
}

.backdrop-transition-leave-active {
  transition: opacity 0.2s ease-in-out;
}

.backdrop-transition-enter-from,
.backdrop-transition-leave-to {
  opacity: 0;
}

/**
 * transition of the half-screen-menus on desktop
 */
.menu-transition-enter-active {
  transition: opacity 0.2s ease-in-out;
}

/* add delay to make going from one menu to another smoother  */
.menu-transition-leave-active {
  transition: opacity 0.2s ease-in-out;
}

.menu-transition-enter-from,
.menu-transition-leave-to {
  opacity: 0;
}
</style>
